import React, {
  useState,
  useEffect,
  useCallback,
  Fragment,
  useContext,
} from 'react';
import { Box, Typography, Divider } from '@mui/material';
import axios from 'axios';
import Swal from 'sweetalert2';
import MainButton from '@/components/UI/mobile/MainButton';
import { ResponseData } from '@/types/api/questionnaire/getQuestionnaireContentFontDesk';
import { QuestionnaireAnswer } from '@/types/api/questionnaire/updateQuestionnaireAnswerFontDesk';
import { QuestionForm } from './types';
import {
  ServiceContainer,
  ServiceTitle,
  VendorContainer,
  VendorTitle,
  QuestionContainer,
  QuestionDescription,
  RateContainer,
  Score,
  QuestionTitle,
} from './styles';
import StarRating from './components/StarRating';
import { PopupContext } from '@/layouts/MobileLayout/MobileLayout';

const QuestionnaireForm: React.FC<{
  form: QuestionForm;
  setForm: React.Dispatch<React.SetStateAction<QuestionForm>>;
  setTrigger: React.Dispatch<React.SetStateAction<Record<string, never>>>;
}> = ({ form, setForm, setTrigger }) => {
  const [questionnaire, setQuestionnaire] = useState<ResponseData | null>(null);
  const [answers, setAnswers] = useState<{
    [questionnaireId: string]: { [topic: string]: number };
  } | null>(null);

  const { setShowPopup } = useContext(PopupContext);

  const getQuestionnaire = useCallback(async () => {
    try {
      const res = await axios.get<ResponseData>(
        '/ezcon/api/getQuestionnaireContentFontDesk',
        {
          params: form.req,
          headers: {
            authorization: localStorage.getItem('feathers-jwt'),
          },
        }
      );
      setQuestionnaire(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [form.req]);

  useEffect(() => {
    if (form.req) {
      getQuestionnaire();
    }
  }, [form.req, getQuestionnaire]);

  useEffect(() => {
    if (questionnaire) {
      const newAnswers: { [key: string]: { [key: string]: number } } = {};
      questionnaire.service.forEach((service) => {
        service.vendors.forEach((vendor) => {
          const answer: { [key: string]: number } = {};
          Object.entries(vendor.questions).forEach(([topic, question]) => {
            answer[topic] = question.answer;
          });
          newAnswers[vendor.questionnaireAnswerId] = answer;
        });
      });
      setAnswers(newAnswers);
    }
  }, [questionnaire]);

  const handleAnswerChange = useCallback(
    (questionnaireAnswerId: string, topic: string, value: number) => {
      setAnswers((prev) => {
        const newAns = JSON.parse(JSON.stringify(prev));
        newAns[questionnaireAnswerId][topic] = value;
        return newAns;
      });
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    if (answers) {
      const params: QuestionnaireAnswer[] = Object.entries(answers).map(
        ([questionnaireAnswerId, answer]) => ({
          questionnaireAnswerId,
          answer,
        })
      );
      try {
        await axios.post(
          '/ezcon/api/updateQuestionnaireAnswerFontDesk',
          params,
          {
            headers: {
              authorization: localStorage.getItem('feathers-jwt'),
            },
          }
        );
        Swal.fire({
          icon: 'success',
          title: '送出成功',
          showConfirmButton: false,
          timer: 1000,
        });
        setForm({ show: false, req: null });
        setTrigger({});
        setShowPopup(undefined);
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: '送出失敗',
          showConfirmButton: false,
          timer: 1000,
        });
        console.error(err);
      }
    }
  }, [answers, setForm, setShowPopup, setTrigger]);

  return (
    <Box
      id="outsideBox"
      sx={{
        position: 'fixed',
        top: 'calc(var(--header-height))',
        left: 0,
        height: 'calc(100vh - var(--header-height))',
        width: '100vw',
        padding: '6vw',
        pt: 0,
        display: 'flex',
        backgroundColor: '#EFEFEF',
        '@media (orientation: landscape)': {
          padding: '0 3.84vw 3.84vw 3.84vw',
          overflowY: 'auto',
        },
      }}
    >
      {questionnaire && (
        <Box
          id="insideBox"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              height: '11.4vw',
              justifyContent: 'center',
              alignItems: 'center',
              '@media (orientation: landscape)': {
                height: '5.7vw',
              },
            }}
          >
            <Typography
              sx={{
                fontSize: '4.4vw',
                lineHeight: '6.7vw',
                color: '#1F2D49',
                fontWeight: 'medium',
                '@media (orientation: landscape)': {
                  fontSize: '2.4vw',
                  lineHeight: '3.3vw',
                },
              }}
            >
              {questionnaire.title}
            </Typography>
          </Box>
          {/* 服務 */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              my: '5.6vw',
              height: '0',
              flexGrow: 1,
              overflowY: 'auto',
              '@media (orientation: landscape)': {
                my: '4.1vw',
              },
            }}
          >
            {questionnaire &&
              answers &&
              questionnaire.service.map((service, sIndex, sArray) => (
                <Fragment key={sIndex}>
                  <ServiceContainer>
                    <ServiceTitle>{service.name}</ServiceTitle>
                    {service.vendors.map((vendor, vIndex) => (
                      <VendorContainer key={vIndex}>
                        <VendorTitle>{vendor.name}</VendorTitle>
                        {Object.entries(vendor.questions).map(
                          ([topic, question], qIndex) => {
                            const ans = answers[vendor.questionnaireAnswerId];
                            return (
                              <QuestionContainer key={qIndex}>
                                <QuestionTitle>{`${
                                  qIndex + 1
                                }.\u00A0`}</QuestionTitle>
                                <Box
                                  sx={{
                                    width: '100%',
                                  }}
                                >
                                  <QuestionDescription>
                                    {question.description}
                                  </QuestionDescription>
                                  <RateContainer>
                                    <StarRating
                                      value={(ans && ans[topic]) || null}
                                      onChange={(value) => {
                                        if (questionnaire.editable) {
                                          handleAnswerChange(
                                            vendor.questionnaireAnswerId,
                                            topic,
                                            value
                                          );
                                        }
                                      }}
                                      editable={questionnaire.editable}
                                    />
                                    <Score>
                                      {ans[topic] && `${ans[topic]}分`}
                                    </Score>
                                  </RateContainer>
                                </Box>
                              </QuestionContainer>
                            );
                          }
                        )}
                      </VendorContainer>
                    ))}
                  </ServiceContainer>
                  {sIndex !== sArray.length - 1 && (
                    <Divider
                      sx={{
                        my: '11.2vw',
                        borderColor: '#383838',
                        '@media (orientation: landscape)': {
                          my: '5.6vw',
                        },
                      }}
                    />
                  )}
                </Fragment>
              ))}
          </Box>
          {questionnaire.editable && (
            <MainButton
              disableRipple
              sx={{
                alignSelf: 'center',
              }}
              onClick={handleSubmit}
            >
              送出
            </MainButton>
          )}
          {!questionnaire.editable && (
            <MainButton
              disableRipple
              sx={{
                alignSelf: 'center',
              }}
              func="close"
              onClick={() => {
                setForm({ show: false, req: null });
                setShowPopup(undefined);
              }}
            >
              關閉
            </MainButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default QuestionnaireForm;
